import React, { useState, useEffect, useCallback, useMemo } from 'react';
import WizardStepper from './components/WizardStepper';
import SessionSelector from './components/SessionSelector';
import StepSetup from './components/StepSetup';
import StepBrief from './components/StepBrief';
import StepPlan from './components/StepPlan';
import StepGenerate from './components/StepGenerate';
import StepResults from './components/StepResults';
import AgentDrawer from './components/AgentDrawer';
import StatusBanners from './components/StatusBanners';
import { AGENT_PERSONAS } from './constants';
import {
  brandTeamService,
  type BrandTeamSession,
  type BrandTeamStatus,
  type BrandTeamOutput,
  type BrandTeamAgentRun,
  type BrandTeamAgentLog,
  type BrandTeamAgentArtifact,
  type BrandTeamChatMessage,
  type CreateBrandTeamSessionRequest,
  type BrandTeamSessionMode,
} from '../../../services/brandTeamService';

function sessionToStep(session: BrandTeamSession | null): number {
  if (!session) return 1;
  const s = session.status;
  if (s === 'generated') return 5;
  if (s === 'generating') return 4;
  if (s === 'plan_approved') return 4;
  if (s === 'brief_approved') return 3;
  return 2;
}

function completedStepsForSession(session: BrandTeamSession | null): number[] {
  if (!session) return [];
  const steps: number[] = [1];
  if (session.briefApprovedAt) steps.push(2);
  if (session.planApprovedAt) steps.push(3);
  if (session.status === 'generated') steps.push(4);
  return steps;
}

const DEFAULT_FORM: CreateBrandTeamSessionRequest = {
  mode: 'full-brand-pack' as BrandTeamSessionMode,
  direction: 'safe',
  businessDescription: '',
  objectives: '',
  assetIds: [],
  useSavedBrandSettings: false,
  mockupPreferences: { aspectRatio: 'auto', includeBrandName: true, includeTagline: false, styleNotes: '', mockupStyle: 'auto', sceneDescription: '' },
};

const BrandTeamWizard: React.FC = () => {
  // --- Core state ---
  const [currentStep, setCurrentStep] = useState(1);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [activeSession, setActiveSession] = useState<BrandTeamSession | null>(null);
  const [sessions, setSessions] = useState<BrandTeamSession[]>([]);
  const [formState, setFormState] = useState<CreateBrandTeamSessionRequest>(DEFAULT_FORM);
  const [actionLoading, setActionLoading] = useState<'launch' | 'brief' | 'plan' | 'generate' | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [sendingChat, setSendingChat] = useState(false);
  const [shufflingIndex, setShufflingIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<BrandTeamStatus | null>(null);
  const [assets] = useState<Array<{ id: string; name: string }>>([]);

  // --- Derived data ---
  const agentRuns: BrandTeamAgentRun[] = useMemo(() => activeSession?.agentRuns || [], [activeSession]);
  const agentLogs: BrandTeamAgentLog[] = useMemo(() => activeSession?.agentLogs || [], [activeSession]);
  const agentArtifacts: BrandTeamAgentArtifact[] = useMemo(() => activeSession?.agentArtifacts || [], [activeSession]);
  const chatMessages: BrandTeamChatMessage[] = useMemo(() => activeSession?.chatMessages || [], [activeSession]);
  const outputs: BrandTeamOutput[] = useMemo(() => activeSession?.outputs || [], [activeSession]);
  const completedSteps = useMemo(() => completedStepsForSession(activeSession), [activeSession]);

  // --- Init: check status + load sessions ---
  useEffect(() => {
    brandTeamService.getStatus().then(setStatus).catch(() => {});
    brandTeamService.listSessions().then(setSessions).catch(() => {});
  }, []);

  // --- Polling ---
  useEffect(() => {
    if (!activeSessionId) return;
    const shouldPoll =
      actionLoading === 'launch' ||
      actionLoading === 'brief' ||
      actionLoading === 'plan' ||
      activeSession?.status === 'planning' ||
      activeSession?.status === 'generating';

    if (!shouldPoll) return;

    const interval = setInterval(async () => {
      try {
        const updated = await brandTeamService.getSession(activeSessionId);
        setActiveSession(updated);

        const targetStep = sessionToStep(updated);
        setCurrentStep((prev) => Math.max(prev, targetStep));

        if (updated.status !== 'planning' && updated.status !== 'generating') {
          setActionLoading(null);
        }
      } catch {
        // Polling failure is non-fatal
      }
    }, 4000);

    return () => clearInterval(interval);
  }, [activeSessionId, actionLoading, activeSession?.status]);

  // --- Actions ---
  const handleLaunch = useCallback(async () => {
    setError(null);
    setActionLoading('launch');
    try {
      const session = await brandTeamService.createSession(formState);
      setActiveSession(session);
      setActiveSessionId(session.id);
      setSessions((prev) => [session, ...prev]);
      setCurrentStep(2);
      setActionLoading(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to create session');
      setActionLoading(null);
    }
  }, [formState]);

  const handleApproveBrief = useCallback(async () => {
    if (!activeSessionId) return;
    setActionLoading('brief');
    try {
      const updated = await brandTeamService.approveBrief(activeSessionId);
      setActiveSession(updated);
      setActionLoading(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to approve brief');
      setActionLoading(null);
    }
  }, [activeSessionId]);

  const handleApprovePlan = useCallback(async () => {
    if (!activeSessionId) return;
    setActionLoading('plan');
    try {
      const updated = await brandTeamService.approvePlan(activeSessionId);
      setActiveSession(updated);
      setActionLoading(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to approve plan');
      setActionLoading(null);
    }
  }, [activeSessionId]);

  const handleGenerate = useCallback(async () => {
    if (!activeSessionId) return;
    setActionLoading('generate');
    try {
      const updated = await brandTeamService.generateSession(activeSessionId);
      setActiveSession(updated);
    } catch (err: any) {
      setError(err?.message || 'Failed to start generation');
      setActionLoading(null);
    }
  }, [activeSessionId]);

  const handleChatSend = useCallback(async () => {
    if (!activeSessionId || !chatInput.trim()) return;
    const message = chatInput.trim();
    setChatInput('');
    setSendingChat(true);
    try {
      const updated = await brandTeamService.chatWithBrandManager(activeSessionId, message);
      setActiveSession(updated);
    } catch {
      // Chat failure is non-fatal
    } finally {
      setSendingChat(false);
    }
  }, [activeSessionId, chatInput]);

  const handleShuffleDeliverable = useCallback(async (index: number) => {
    if (!activeSessionId) return;
    setShufflingIndex(index);
    try {
      const updated = await brandTeamService.shuffleDeliverable(activeSessionId, index);
      setActiveSession(updated);
    } catch (err: any) {
      setError(err?.message || 'Failed to shuffle deliverable');
    } finally {
      setShufflingIndex(null);
    }
  }, [activeSessionId]);

  const handleSelectSession = useCallback(async (id: string) => {
    try {
      const session = await brandTeamService.getSession(id);
      setActiveSessionId(id);
      setActiveSession(session);
      setCurrentStep(sessionToStep(session));
    } catch (err: any) {
      setError(err?.message || 'Failed to load session');
    }
  }, []);

  const handleNewSession = useCallback(() => {
    setActiveSessionId(null);
    setActiveSession(null);
    setCurrentStep(1);
    setFormState(DEFAULT_FORM);
    setError(null);
  }, []);

  const handleStepClick = useCallback((step: number) => {
    if (completedSteps.includes(step) || step === currentStep) {
      setCurrentStep(step);
    }
  }, [completedSteps, currentStep]);

  const handleFormChange = useCallback((updates: Partial<CreateBrandTeamSessionRequest>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  }, []);

  // --- Agent drawer trigger ---
  const activeAgentCount = agentRuns.filter((r) => r.status === 'running').length;
  const doneAgentCount = AGENT_PERSONAS.filter((p) =>
    agentRuns.find((r) => r.agentKey === p.agentKey && r.status === 'completed')
  ).length;

  return (
    <div className="flex h-full flex-col bg-gray-50">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-primary-100 bg-white px-4 py-2.5">
        <SessionSelector
          sessions={sessions}
          activeSessionId={activeSessionId}
          onSelectSession={handleSelectSession}
          onNewSession={handleNewSession}
        />

        <button
          type="button"
          onClick={() => setDrawerOpen(true)}
          className="relative flex items-center gap-2 rounded-xl border border-primary-100 bg-white px-3 py-2 text-xs font-medium text-text-dark shadow-sm hover:bg-gray-50 transition-colors"
        >
          <div className="flex -space-x-1.5">
            {AGENT_PERSONAS.slice(0, 3).map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.agentKey} className={`flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br ${p.gradientFrom} ${p.gradientTo} ring-2 ring-white`}>
                  <Icon className="h-3 w-3 text-white" />
                </div>
              );
            })}
          </div>
          <span className="hidden sm:inline">AI Team</span>
          {activeAgentCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-4 w-4">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand-purple opacity-40" />
              <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-brand-purple text-[8px] font-bold text-white">
                {activeAgentCount}
              </span>
            </span>
          )}
          {doneAgentCount > 0 && activeAgentCount === 0 && (
            <span className="rounded-full bg-green-50 px-1.5 py-0.5 text-[9px] font-semibold text-green-600">
              {doneAgentCount}/{AGENT_PERSONAS.length}
            </span>
          )}
        </button>
      </div>

      {/* Status banners */}
      <div className="px-4 pt-2 space-y-2">
        <StatusBanners schemaStatus={status} error={error} />
      </div>

      {/* Stepper */}
      <div className="border-b border-primary-100 bg-white">
        <WizardStepper
          currentStep={currentStep}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto">
        {currentStep === 1 && (
          <StepSetup
            formState={formState}
            onFormChange={handleFormChange}
            onLaunch={handleLaunch}
            launching={actionLoading === 'launch'}
            availableAssets={assets}
          />
        )}
        {currentStep === 2 && activeSession && (
          <StepBrief
            session={activeSession}
            agentLogs={agentLogs}
            onApprove={handleApproveBrief}
            approving={actionLoading === 'brief'}
            onNext={() => setCurrentStep(3)}
          />
        )}
        {currentStep === 3 && activeSession && (
          <StepPlan
            session={activeSession}
            agentLogs={agentLogs}
            onApprove={handleApprovePlan}
            approving={actionLoading === 'plan'}
            onNext={() => setCurrentStep(4)}
            onShuffle={handleShuffleDeliverable}
            shufflingIndex={shufflingIndex}
          />
        )}
        {currentStep === 4 && activeSession && (
          <StepGenerate
            session={activeSession}
            outputs={outputs}
            agentLogs={agentLogs}
            onGenerate={handleGenerate}
            generating={actionLoading === 'generate'}
            onViewResults={() => setCurrentStep(5)}
          />
        )}
        {currentStep === 5 && activeSession && (
          <StepResults
            outputs={outputs}
            onNewSession={handleNewSession}
          />
        )}
      </div>

      {/* Agent Drawer */}
      <AgentDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        agentRuns={agentRuns}
        agentArtifacts={agentArtifacts}
        agentLogs={agentLogs}
        chatMessages={chatMessages}
        chatInput={chatInput}
        onChatInputChange={setChatInput}
        onChatSend={handleChatSend}
        chatSending={sendingChat}
        chatDisabled={!activeSessionId}
      />
    </div>
  );
};

export default BrandTeamWizard;
